import {
	getCameraCaptureHeight,
	isHighSpecSender,
} from '../../../features/base/environment/videoCapability';
import JitsiMeetJS, { JitsiConnectionQualityEvents } from '../../../features/base/lib-jitsi-meet';
import { createLocalTrack } from '../../../features/base/lib-jitsi-meet/functions.any';
import { MEDIA_TYPE } from '../../../features/base/media';
import { getLocalMember, ROLE } from '../../../features/base/members';
import { getLocalTracks, getTrackState } from '../../../features/base/tracks';
import { getWebsocketURLBase } from '../../../features/base/ui/utils';
import { startListeningForStats } from '../../../features/connection-indicator';
import { getMediaServerInfo } from '../../../features/room';
import {
	startReceiveQualityMonitor,
	stopReceiveQualityMonitor,
} from '../../../features/video-layout/receiveQualityMonitor';
import { destroyLocalTracks } from '../../../features/base/tracks';
import JitsiConference from './conference';
import { default as Connection } from './connection';
import { default as ScreenShare } from './screenSharing';
import senderHealth from './senderHealth';
import JitsiTracks from './tracks';

const config = {
	connect: {
		serviceUrl: `wss://mate3.dev.meetmate.co.kr/jitsi-1/xmpp-websocket?1716794433000`,
		hosts: {
			domain: 'mate.net',
			muc: 'muc.mate.net',
		},
	},
	conference: {
		p2p: false,
		enableUnifiedOnChrome: true,
		enableForcedReload: false,
		enableNoisyMicDetection: false,
		// ICE 실패 시 lib 가 Jicofo 에 세션 재초대(ICE 재시작)를 요청한다(서버팀 확인: Jicofo 재초대 지원, 2026-09-21).
		// 미설정 시 lib 은 상황에 따라 CONFERENCE_FAILED(ICE_FAILED)만 내고 끝나, 강사 송출 경로가 죽어도
		// 아무 복구가 없었다(강사 화면 검정 수 분 지속 사고). 앱 측 후속 복구는 senderHealth.js 참고.
		enableIceRestart: true,
		openBridgeChannel: 'websocket',
		videoQuality: {
			maxBitratesVideo: {
				low: 100000,
				standard: 500000,
				high: 2000000,			
			},
		},
	},
};

class JitsiManagement {
	constructor() {
		this._localTracksInitialized = false;
		this.Connection = new Connection();
		this.Conference = new JitsiConference();
		this.Tracks = new JitsiTracks();

	}

	setConstraints(data) {
		if (this.Conference) {
			this.Conference.setReceiverConstraints(data);
		}
	}

	// 회의실 connect
	connectAndJoin(isTemp, user_id) {
		if (this.Connection.connection) {
			return Promise.resolve(this.Conference.conference);
		}

		const connectConfig = Object.assign({}, config.connect);
		const mediaInfo = getMediaServerInfo(APP.store.getState);

		connectConfig.serviceUrl = `${getWebsocketURLBase()}${mediaInfo.url}?${Date.parse(new Date())}`;

		return new Promise((resolve, reject) => {
			ScreenShare.init(this.createScreenShareTrack.bind(this));

			// 회의실 입장을 위한 jitsi connect
			this.Connection.connect(connectConfig).then(connection => {
				const conferenceConfig = location.search.includes('disableSimulcast')
					? { disableSimulcast: true }
					: { disableSimulcast: false };

				const joinConfig = Object.assign(conferenceConfig, config.conference);

				// 호스트(1080 송출, 계층 540/720/1080)는 계층별 비트레이트를 상향한다.
				// 계층 간 간격 1M→2.5M(2.5배)→4M(1.6배): BWE가 회선에 맞는 계단을
				// 고를 수 있게 한다. 합산 업링크 ~7.5M — 강사 회선 업로드 10M+ 권장.
				if (getLocalMember(APP.store.getState)?.role === ROLE.HOST && isHighSpecSender()) {
					joinConfig.videoQuality = {
						maxBitratesVideo: {
							low: 1000000,
							standard: 2500000,
							high: 4000000,
						},
					};
				}

				// 회의실 입장
				const handler = {
					handlerCreateTracks: options => this.Tracks.createTracks(options),
				};

				return this.Conference.join(connection, joinConfig, handler, isTemp, user_id)
					.then(conference => {
						!isTemp && APP.store.dispatch(startListeningForStats(conference));
						// 1080 수신 기기의 디코딩 부하 감시 (부하 지속 시 720으로 강등)
						!isTemp && startReceiveQualityMonitor(conference);
						// 내 미디어 연결 감시 + 송출자 자가 복구(강사 화면 검정 사고 대책)
						!isTemp && senderHealth.attach(conference);
						this.conference = conference;

						resolve(conference);
					})
					.catch(err => reject(err));
			});
		});
	}

	/**
	 * 전체 재접속(leave + disconnect + rejoin). Video.tsx 프리즈 복구와 senderHealth 가 호출한다.
	 *
	 * - 동시 호출 가드: 스테이지 타일이 여럿이거나 감시 모듈이 겹쳐 호출해도 재접속은 한 번만 진행하고
	 *   같은 Promise 를 돌려준다(이중 leave 로 인한 상태 꼬임 방지).
	 * - 로컬 트랙 정리: 종전에는 leave 후 _conferenceWillJoin 이 카메라/마이크를 "새로" 만들면서
	 *   기존 JitsiLocalTrack 은 파기되지 않아 재접속마다 캡처가 하나씩 누적됐다(카메라 점유·CPU 누수,
	 *   학생은 프리즈 복구 사이클마다 반복). 새 트랙을 만들기 전에 기존 로컬 트랙을 파기한다.
	 */
	async reconnect(isTemp, user_id) {
		if (this._reconnecting) return this._reconnecting;

		this._reconnecting = (async () => {
			senderHealth.detach();
			// Leave current conference if joined
			if (this.Conference && this.Conference.conference) {
				await this.Conference.leave();
			}
			try {
				await APP.store.dispatch(destroyLocalTracks());
			} catch (e) {
				console.warn('[reconnect] 로컬 트랙 정리 실패(계속 진행):', e);
			}
			// Disconnect from server
			if (this.Connection && this.Connection.connection) {
				await this.Connection.disconnect();
			}
			// Reconnect and rejoin
			return this.connectAndJoin(isTemp, user_id);
		})().finally(() => {
			this._reconnecting = null;
		});

		return this._reconnecting;
	}

	async conferenceAddedTrack(mediaType) {
		const localTracks = getLocalTracks(getTrackState(APP.store.getState)) || [];

		// 청취자는 입장 시 마이크를 보유하지 않는다. 오디오 발언 허가인데 로컬 오디오 트랙이 없으면
		// 즉석에서 마이크를 획득해 회의에 송출하고 서버 bridge(audio_track_id)를 갱신한다.
		// (HOST/individualLive 는 항상 오디오 트랙이 있어 이 분기에 안 들어옴 → 기존 동작 유지)
		if (mediaType === MEDIA_TYPE.AUDIO && !localTracks.some(t => t && t.mediaType === MEDIA_TYPE.AUDIO)) {
			// ★레이스 가드: HOST 입장 직후 SET_AUDIO_MUTED(false)가 redux(addLocalTrack) 등록
			// "전"에 도착하면 redux 는 비어 있지만 회의는 이미 오디오 트랙을 보유한 상태다.
			// 이때 즉석 획득으로 가면 두 번째 마이크 getUserMedia → addTrack 이
			// 'Cannot add second audio track' 으로 실패하고 획득한 마이크가 누수된다(실측 로그).
			// 회의 보유 트랙이 있으면 그 트랙 unmute 로 의도를 완성하고 끝낸다.
			const confAudio = this.Conference?.conference?.getLocalTracks?.(MEDIA_TYPE.AUDIO)?.[0];
			if (confAudio) {
				try {
					await confAudio.unmute();
				} catch (err) {
					console.error('[audio] 기존 회의 오디오 트랙 unmute 실패:', err);
				}
				return;
			}

			await this.Conference.acquireAndPublishAudio();

			return;
		}

		const handleTrack = async track => {
			if (!track) return;

			// ★unmute 완료를 기다린 뒤 addTrack 한다. 비디오 mute 는 카메라 스트림 자체를
			// 해제하므로(doesVideoMuteByStreamRemove) unmute = 카메라 재획득(getUserMedia)이다.
			// 태블릿/모바일은 카메라 open 이 수 초 걸리는데, 기존처럼 unmute 미대기 상태로
			// addTrack 하면 "소스는 시그널링됐는데 프레임 없음" 구간이 길어져(이중 재협상)
			// 손들기 수락 후 상대 화면 로딩이 더 오래 걸렸다. 라이브 스트림으로 한 번에 재협상.
			try {
				await track.track.unmute();
			} catch (err) {
				console.error('[pin] 트랙 unmute 실패:', err);
			}

			// Add track to the conference
			this.Conference.conference.addTrack(track.track);
		};

		if (localTracks) {
			for (const track of localTracks) {
				if (mediaType) {
					if (mediaType === MEDIA_TYPE.VIDEO && track.mediaType === MEDIA_TYPE.VIDEO) {
						handleTrack(track);
					} else if (mediaType === MEDIA_TYPE.AUDIO && track.mediaType === MEDIA_TYPE.AUDIO) {
						handleTrack(track);
					}
				} else {
					handleTrack(track);
				}
			}
		}
	}

	async conferenceRemovedTrack(mediaType) {
		const localTracks = getLocalTracks(getTrackState(APP.store.getState)) || [];
	
		if (!this.Conference) {
			console.error('Conference is not initialized.');
			return;
		}
	
		// 청취자는 발언 회수 시 마이크를 반납(파기)해 다시 듣기 상태에서 통화모드/BT SCO 재진입을 막는다.
		// HOST/individualLive 는 기존대로 유지(즉시 재발언 대비).
		const isListener = this.Conference._isListener();

		const handleTrack = async (track) => {
			if (track && track.track && typeof track.track.mute === 'function') {
				try {
					if (track.mediaType === MEDIA_TYPE.AUDIO) {
						if (isListener) {
							await this.Conference.releaseAudio(track);

							return;
						}

						track.track.mute();
					}

					await this.Conference.removeTrack(track.track);
				} catch (err) {
					console.error('Failed to remove track:', err);
				}
			} else {
				console.warn('Invalid track or track.track is missing:', track);
			}
		};
	
		if (mediaType && ![MEDIA_TYPE.AUDIO, MEDIA_TYPE.VIDEO].includes(mediaType)) {
			return;
		}
	
		for (const track of localTracks) {
			try {
				if (!mediaType || track.mediaType === mediaType) {
					await handleTrack(track);
				}
			} catch (error) {
				console.error('Error processing track:', track, error);
			}
		}
	}
	
	

	conferenceMutedVideoTrack(isMuted) {
		this.Conference.conferenceMutedVideoTrack(isMuted);
	}

	conferenceReplaceTrack(newTrackId) {
		this.Conference.conferenceReplaceTrack(newTrackId);
	}

	toggleE2EE(isEnable) {
		if (this.Conference && this.Conference.conference) this.Conference.conference.toggleE2EE(isEnable);
	}

	/**
	 * 가벼운 복구: 회의(MUC)를 유지한 채 JVB 미디어 세션만 재시작(lib _restartMediaSessions →
	 * session-terminate(restart) → Jicofo 재초대). 다른 참가자에게 퇴장/입장으로 보이지 않는다.
	 * @returns {boolean} 호출됐는지
	 */
	restartMediaSessions() {
		if (this.Conference && this.Conference.conference && typeof this.Conference.conference._restartMediaSessions === 'function') {
			console.log('restartMediaSessions(light)');
			this.Conference.conference._restartMediaSessions();
			return true;
		}
		return false;
	}

	// 회의실 disconnect
	disconnect() {
		return new Promise((resolve, reject) => {
			stopReceiveQualityMonitor();
			senderHealth.reset();
			this.Conference.leave();
			this.Connection.disconnect();
			resolve();
		});
	}

	/**
	 * 녹화 시작
	 * @returns
	 */
	startScreenShare() {
		return ScreenShare.toggleScreenSharing(true);
	}

	/**
	 * 녹화 종료
	 * @returns
	 */
	stopScreenShare() {
		return ScreenShare.toggleScreenSharing(false);
	}

	createScreenShareTrack(options) {
		return this.Tracks.createScreenShareTrack(options);
	}

	getScreenSharing() {
		return ScreenShare.isScreenSharing();
	}

	async updateTrack(deviceId, type, options) {
		// 카메라 교체 시에도 입장 시와 동일한 캡처 해상도를 유지한다
		// (constraints 미지정 시 lib 기본값 720으로 회귀)
		const trackOptions =
			type === MEDIA_TYPE.VIDEO
				? {
						constraints: {
							video: {
								height: {
									ideal: getCameraCaptureHeight(
										getLocalMember(APP.store.getState)?.role === ROLE.HOST,
									),
								},
								frameRate: { ideal: 30 },
							},
						},
					}
				: undefined;

		let newTrack = null;

		try {
			newTrack = await createLocalTrack(type, deviceId, 5000, trackOptions);
			// ★await 제거 금지(2026-07-20 "장치설정 저장만으로 마이크 켜짐" 실사고).
			// lib 의 mute() 는 프라미스 체인(_queueSetMuted → _prevSetMuted.then)이라 반영이 최소
			// 1 마이크로태스크 뒤인데, useAudio/VideoStream → addLocalTrack/replaceStoredTracks 의
			// isMuted() 판독은 같은 동기 스택(TaskQueue 유휴 시 동기 실행)이라 미대기 시 "항상"
			// unmuted 로 오독된다. 그 값이 setAudioMuted(false) 전역 액션으로 방송되면
			// conferenceAddedTrack 이 기존 트랙 unmute 또는 즉석획득(acquireAndPublishAudio)으로
			// 마이크를 켜 버린다(전 플랫폼 결정적 재현, 서버 audio_muted=false 로그).
			if (options?.muted !== undefined && options.muted && newTrack) await newTrack.mute();
		} catch (err) {
			// 호출부(미들웨어)가 await 하지 않아 이 rejection 은 표면화되지 않는다 — 로그가 유일한 흔적.
			console.error(`updateTrack 실패: type=${type}, deviceId=${deviceId}`, err);
			// 스트림 교체에 넘기기 "전" 실패 시 획득 장치를 반납한다 — 방치하면 회의에 안 실린 채
			// 마이크/카메라 점유(통화모드/BT SCO 진입 위험)가 지속된다. createLocalTrack 실패는
			// newTrack=null 이라 자동 스킵. ※dispose 는 이 try(생성/뮤트) 실패에만 적용해야 한다 —
			// 교체 큐에 넘긴 뒤 dispose 하면 진행 중인 replaceTrack 이 disposed 트랙으로 실패하므로
			// useVideoStream/useAudioStream 호출을 try 밖에 둔다(구조로 강제).
			if (newTrack) {
				try { await newTrack.dispose(); } catch (e) { /* 무시 */ }
			}
			throw err;
		}

		if (type === MEDIA_TYPE.VIDEO) {
			this.Conference.useVideoStream(newTrack);
		} else {
			this.Conference.useAudioStream(newTrack);
		}
	}

	replaceTrack(oldTrack, newTrack) {
		if (!newTrack && oldTrack) {
			this.Conference.removeTrack(oldTrack);
			return;
		}

		try {
			return this.Conference.replaceTrack(oldTrack, newTrack);
		} catch (err) {
			APP.UI.alertMessage('트랙 교체에 실패하였습니다.');
			return false;
		}
	}

	async useVideoStream(videoTrack) {
		await this.Conference.useVideoStream(videoTrack);
	}

	/**
	 * AddEventListener
	 */
	/**
	 * 로컬 사용자의 연결 상태 이벤트 등록
	 * @param {function} handler
	 */
	registerUpdateLocalStats(handler) {
		const conference = this.Conference.conference;
		conference && conference.on(JitsiConnectionQualityEvents.LOCAL_STATS_UPDATED, handler);
	}

	/**
	 * remote 사용자의 연결 상태 이벤트 등록
	 * @param {function} handler
	 */
	registerUpdateRemoteStats(handler) {
		const conference = this.Conference.conference;

		conference.on(JitsiConnectionQualityEvents.REMOTE_STATS_UPDATED, handler);
	}

	/**
	 * Audio Output
	 */
	getAudioOutputDeviceId() {
		return JitsiMeetJS.mediaDevices.getAudioOutputDevice();
	}
	async setAudioOutputDevice(newId = '') {
		JitsiMeetJS.mediaDevices.setAudioOutputDevice(newId).catch(err => {
			
		});
	}
}

export default JitsiManagement;
